import { CompanyAddress } from '../../company-profile-data/models/company';

export interface ProductionCharacteristic {
  requestId: number;
  clientInfo: ClientInfo;
  personInfo: PersonInfo;
  createDate: string | null;
}

export interface ClientInfo {
  name: string | null;
  companyNumber: string | null;
  economicalActivity: string;
  kID: string | null;
  address: CompanyAddress;
  employees: string[] | null;
  femaleEmployees?: string[] | null;
}

export interface PersonInfo {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  sSN: string | null;
  experienceSum: string | null;
  additionalInfo: string | null;
  jobsHistory: JobsHistory[];
  temporaryIncapacity: TemporaryIncapacity[];
  currentJobInfo: JobInfo;
  otherJobInfo: JobInfo;
}

export interface JobsHistory {
  startDate: string | null;
  endDate: string | null;
  companyName: string | null;
  companyAddress: string | null;
  position: string | null;
  experience: string | null;
}

export interface JobInfo {
  position: string | null;
  nKPD: string | null;
  positionExperience: string | null;
  positionExperienceInCurrentCompany: string | null;
  laborCategory: string | null;
  workingTimeDuration: string | null;
  isNightWorking: boolean | null;
  isShiftWorking: boolean | null;
  isPermanent: boolean | null;
  workPlaceType: string | null;
  workingPlaceLocation: string | null;
  workingPlaceLocationOtherDescription: string | null | null;
  workingEnvironmentFactors: WorkingEnvironmentFactor[];
  workingProcessFactors: WorkingProcessFactors;
}

export interface WorkingEnvironmentFactor {
  factor: string;
  riskAssessment: string;
  exposition: string;
  expositionInYears: string;
  measuresTakenToLimitTheImpact: string;
}

export interface WorkingProcessFactors {
  legs: string;
  leftArm: {
    fingers: boolean;
    wrist: boolean;
    forearm: boolean;
    arm: boolean;
    shoulder: boolean;
  };
  rightArm: {
    fingers: boolean;
    wrist: boolean;
    forearm: boolean;
    arm: boolean;
    shoulder: boolean;
  };
  spine: {
    neck: boolean;
    chest: boolean;
    belt: boolean;
    waist: boolean;
  };
  workZone: number;
  factor: {
    riskAssessment: string;
    otherInfo: string;
    exposition: string;
    expositionInYears: string;
    measuresTakenToLimitTheImpact: string;
  };
  independently: number;
  distance: string;
  weightsSingle: string;
  weightsTotal: string;
  timeBetweenOps: string;
  keepWeights: string;
  pushWeights: string;
  pushDistance: string;
  pushVehicle: string;
  isMonotonousWork: boolean;
  workPosition: {
    shortSpace: boolean;
    standing: boolean;
    sitting: boolean;
    bend: boolean;
    knee: boolean;
    elbowPressure: boolean;
    wristPressure: boolean;
    feetFingersPressure: boolean;
    highHands: boolean;
    other: boolean;
    shortSpaceHours: string;
    standingHours: string;
    sittingHours: string;
    bendHours: string;
    kneeHours: string;
    elbowPressureHours: string;
    wristPressureHours: string;
    feetFingersPressureHours: string;
    highHandsHours: string;
    otherHours: string;
  };
  modeOption: number;
  effort: {
    voice: boolean;
    visual: string;
    psycho: string;
    other: string;
    voiceHours: string;
    visualHours: string;
    psychoHours: string;
    otherHours: string;
  };
  defenceTools: string;
  defencePersonalTools: string;
}

export interface TemporaryIncapacity {
  firstYear: string;
  firstMKB: string;
  firstTotalDays: string;
  secondYear: string;
  secondMKB: string;
  secondTotalDays: string;
}

export const toRequestDetailsDTO = (data: any) => {
  const result: ProductionCharacteristic = {
    //first 2 missing
    requestId: data.requestId,
    createDate: null,
    clientInfo: {
      companyNumber: data.companyDetails?.companyNumber,
      kID: data.companyDetails?.kID,
      name: data.companyDetails?.name,
      economicalActivity: data.companyDetails?.economicalActivity,
      address: data.companyDetails?.address,
      employees: null,
      femaleEmployees: null,
    },
    personInfo: {
      firstName: data.personDetails?.firstName,
      middleName: data.personDetails?.middleName,
      lastName: data.personDetails?.lastName,
      sSN: data.personDetails?.egn,
      experienceSum: data?.personDetails?.jobsHistory
        .map((item: any) => Number.parseInt(item.experience))
        .reduce((acc: any, curr: any) => acc + curr, 0)
        .toString(),
      additionalInfo: data.additionalInfo,
      temporaryIncapacity: data.incapacityOfWorkDetails?.rows,
      jobsHistory: data?.personDetails?.jobsHistory,
      currentJobInfo: {
        position: data.professionDetails?.position,
        nKPD: data.professionDetails?.nKPD?.id,
        positionExperience: data.professionDetails?.positionExperience,
        positionExperienceInCurrentCompany:
          data.professionDetails?.positionExperienceInCurrentCompany,
        laborCategory: data.professionDetails?.laborCategory,
        workingTimeDuration: data.professionDetails?.workingTimeDuration,
        isNightWorking: data.professionDetails?.isNightWorking,
        isShiftWorking: data.professionDetails?.isShiftWorking,
        isPermanent: data.professionDetails?.isPermanent,
        workPlaceType: data.professionDetails?.workPlaceType,
        workingPlaceLocation: data.professionDetails?.workingPlaceLocation,
        workingPlaceLocationOtherDescription: null,
        workingEnvironmentFactors:
          data.workEnvironmentsDetails?.factorDetails?.map((p: any) => {
            return p.details;
          }),
        workingProcessFactors: {
          leftArm: data.workProcessDetails?.leftArm,
          rightArm: data.workProcessDetails?.rightArm,
          legs: data.workProcessDetails?.legs,
          spine: data.workProcessDetails?.spine,
          workZone: data.workProcessDetails?.workZone,
          factor: data.workProcessDetails?.factor,
          independently: data.workProcessDetails?.independently,
          distance: data.workProcessDetails?.distance,
          weightsSingle: data.workProcessDetails?.weightsSingle,
          weightsTotal: data.workProcessDetails?.weightsTotal,
          timeBetweenOps: data.workProcessDetails?.timeBetweenOps,
          keepWeights: data.workProcessDetails?.keepWeights,
          pushWeights: data.workProcessDetails?.pushWeights,
          pushDistance: data.workProcessDetails?.pushDistance,
          pushVehicle: data.workProcessDetails?.pushVehicle,
          isMonotonousWork: data.workProcessDetails?.isMonotonousWork,
          workPosition: data.workProcessDetails?.workPosition,
          modeOption: data.workProcessDetails?.modeOption,
          effort: data.workProcessDetails?.effort,
          defenceTools: data.workProcessDetails?.defenceTools,
          defencePersonalTools: data.workProcessDetails?.defencePersonalTools,
        },
      },
      otherJobInfo: {
        position: data.professionDetailsA?.position,
        nKPD: data.professionDetailsA?.nKPD?.id,
        positionExperience: data.professionDetailsA?.positionExperience,
        positionExperienceInCurrentCompany:
          data.professionDetailsA?.positionExperienceInCurrentCompany,
        laborCategory: data.professionDetailsA?.laborCategory,
        workingTimeDuration: data.professionDetailsA?.workingTimeDuration,
        isNightWorking: data.professionDetailsA?.isNightWorking,
        isShiftWorking: data.professionDetailsA?.isShiftWorking,
        isPermanent: data.professionDetailsA?.isPermanent,
        workPlaceType: data.professionDetails?.workPlaceType,
        workingPlaceLocation: data.professionDetailsA?.workingPlaceLocation,
        workingPlaceLocationOtherDescription: null,
        workingEnvironmentFactors:
          data.workEnvironmentsDetailsA?.factorDetails?.map((p: any) => {
            return p.details;
          }),
        workingProcessFactors: {
          leftArm: data.workProcessDetailsA?.leftArm,
          rightArm: data.workProcessDetailsA?.rightArm,
          legs: data.workProcessDetailsA?.legs,
          spine: data.workProcessDetailsA?.spine,
          workZone: data.workProcessDetailsA?.workZone,
          factor: data.workProcessDetailsA?.factor,
          independently: data.workProcessDetailsA?.independently,
          distance: data.workProcessDetailsA?.distance,
          weightsSingle: data.workProcessDetailsA?.weightsSingle,
          weightsTotal: data.workProcessDetailsA?.weightsTotal,
          timeBetweenOps: data.workProcessDetailsA?.timeBetweenOps,
          keepWeights: data.workProcessDetailsA?.keepWeights,
          pushWeights: data.workProcessDetailsA?.pushWeights,
          pushDistance: data.workProcessDetailsA?.pushDistance,
          pushVehicle: data.workProcessDetailsA?.pushVehicle,
          workPosition: data.workProcessDetailsA?.workPosition,
          modeOption: data.workProcessDetailsA?.modeOption,
          effort: data.workProcessDetailsA?.effort,
          defenceTools: data.workProcessDetailsA?.defenceTools,
          defencePersonalTools: data.workProcessDetailsA?.defencePersonalTools,
          isMonotonousWork: data.workProcessDetailsA?.isMonotonousWork,
        },
      },
    },
  };

  return result;
};
